import { Alert, Flex, Heading, Pill, Text } from "@instructure/ui";
import { t } from "i18next";
import { useContext } from "react";
import Skeleton from "react-loading-skeleton";
import { useOutletContext } from "react-router-dom";
import RichTextEditor from "react-rte";
import { Card } from "../../../../../components/Card";
import ProgressStepper from "../../../../../components/ProgressStepper";
import { UserDetailContext } from "../../../../../utils/Context";
import { formatToReadableTime } from "../../../../../utils/utils";
import { ASSIGNMENT, getFinalizeSteps, LMG, UPLOAD } from "../utils";
import CanvasAssignmentSummary from "./CanvasAssignmentSummary";
import LMGSummary from "./LMGSummary";
import UploadScoresSummary from "./UploadScoresSummary";

const FinalizeSummary = () => {
  const userDetails = useContext(UserDetailContext);
  const { reflections, actions } = useOutletContext();
  const { formData } = useOutletContext();
  const type = formData.type;

  function finalizationText(type) {
    switch (type) {
      case ASSIGNMENT:
        return t("finalizeCourse.canvasAssignment");
      case LMG:
        return t("finalizeCourse.learningMasteryGradebook");
      case UPLOAD:
        return t("finalizeCourse.scoreUpload");
      default:
        return t("global.na");
    }
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <ProgressStepper steps={getFinalizeSteps(t)} currentStep={4} />
      <div
        style={{ flex: 1, overflowY: "auto", padding: "0.75rem 0" }}
        tabIndex={0}
      >
        <Card
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            height: "auto",
            margin: "0 0.75rem 1.5rem 0.75rem",
          }}
        >
          <Heading level="h2" border="bottom">
            {t("finalizeCourse.methodOfFinalization")}
          </Heading>
          <Text>
            <Text weight="bold">{t("finalizeCourse.finalizingOf")}: </Text>
            {finalizationText(type)}
          </Text>
          {type === ASSIGNMENT && (
            <CanvasAssignmentSummary
              assignmentName={formData.metadata.assignmentName}
              results={formData.metadata.results}
            />
          )}
          {type === LMG && (
            <LMGSummary
              roundingMethod={formData.metadata.roundingMethod}
              results={formData.metadata.results}
            />
          )}
          {type === UPLOAD && (
            <UploadScoresSummary
              assignmentName={formData.metadata.assignmentName}
            />
          )}
        </Card>
        <Card style={{ height: "auto", margin: "0 0.75rem" }}>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <div style={{ flex: 1 }}>
              <Heading level="h2" border="bottom" margin="0 0 small 0">
                {t("reflections.reflections")}
              </Heading>
              <Flex gap="medium" wrap="wrap">
                {reflections ? (
                  reflections.length > 0 ? (
                    reflections.map((ref, key) => (
                      <Flex.Item key={key} size="100%">
                        <Card
                          style={{ border: "1px solid #ccc", padding: "10px" }}
                        >
                          <Flex
                            alignItems="start"
                            gap="small"
                            margin="0 0 small 0"
                            wrap="wrap"
                          >
                            <Flex.Item shouldShrink shouldGrow>
                              <Text as="p" fontStyle="italic" size="small">
                                {ref.from_template
                                  ? userDetails.name
                                  : ref.metadata.author}
                                {" - "}
                                {formatToReadableTime(new Date(ref.timestamp))}
                              </Text>
                            </Flex.Item>
                            {ref.is_draft && (
                              <Flex.Item>
                                <Pill color="success">
                                  {t("reflections.draft")}
                                </Pill>
                              </Flex.Item>
                            )}
                          </Flex>
                          {ref.from_template ? (
                            <Flex wrap="wrap" gap="medium">
                              {ref.sections.map((section) => (
                                <Flex.Item key={section.id} width="100%">
                                  <Flex wrap="wrap" gap="small">
                                    <Flex.Item width="100%">
                                      <Text weight="bold">
                                        {section.header_question}
                                      </Text>
                                    </Flex.Item>
                                    <Flex.Item>
                                      <div
                                        style={{
                                          borderLeft: "2px solid #03893D",
                                          paddingLeft: "0.75rem",
                                        }}
                                      >
                                        <RichTextEditor
                                          className="readOnlyClass"
                                          value={RichTextEditor.createValueFromString(
                                            section.content,
                                            "html"
                                          )}
                                          readOnly
                                        />
                                      </div>
                                    </Flex.Item>
                                  </Flex>
                                </Flex.Item>
                              ))}
                            </Flex>
                          ) : (
                            <RichTextEditor
                              className="readOnlyClass"
                              value={RichTextEditor.createValueFromString(
                                ref.content,
                                "html"
                              )}
                              readOnly
                            />
                          )}
                        </Card>
                      </Flex.Item>
                    ))
                  ) : (
                    <Flex.Item size="100%">
                      <Alert>{t("finalizeCourse.noReflections")}</Alert>
                    </Flex.Item>
                  )
                ) : (
                  <Flex.Item size="100%">
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                  </Flex.Item>
                )}
              </Flex>
            </div>
            <div style={{ flex: 1 }}>
              <Heading level="h2" border="bottom" margin="0 0 small 0">
                {t("actions.actions")}
              </Heading>
              <Flex gap="medium" wrap="wrap">
                {actions ? (
                  actions.length > 0 ? (
                    actions.map((action, key) => (
                      <Flex.Item key={key} size="100%">
                        <Card
                          style={{ border: "1px solid #ccc", padding: "10px" }}
                        >
                          <Flex alignItems="start" gap="small" wrap="wrap">
                            <Flex.Item shouldShrink shouldGrow>
                              <Text as="p" fontStyle="italic" size="small">
                                {action.author}
                                {" - "}
                                {formatToReadableTime(
                                  new Date(action.timestamp + "Z")
                                )}
                              </Text>
                            </Flex.Item>
                            <Flex.Item>
                              <Pill color="info">
                                {action.action_type.name}
                              </Pill>
                            </Flex.Item>
                            <Flex.Item size="100%">
                              <Text as="p" size="small">
                                {action.text}
                              </Text>
                            </Flex.Item>
                          </Flex>
                        </Card>
                      </Flex.Item>
                    ))
                  ) : (
                    <Flex.Item size="100%">
                      <Alert>{t("finalizeCourse.noActions")}</Alert>
                    </Flex.Item>
                  )
                ) : (
                  <Flex.Item size="100%">
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                  </Flex.Item>
                )}
              </Flex>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FinalizeSummary;
